import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  ConversationCandidateResponse,
  ConversationSupportAudience,
  GetConversationCandidatesParams,
  getConversation,
  getConversationCandidates,
  openViktorConversation,
  queryKeys,
} from '@vgrubs/components';

/**
 * The two reads both conversation modals and the page itself need.
 *
 * Wrapped as hooks rather than repeated `useQuery` blocks so the key, the
 * unwrapping and the staleness rules are stated once - three copies of
 * `queryKeys.conversations.candidates(...)` is exactly how two callers end up
 * caching the same request under different keys.
 */

/** Candidates go stale slowly; the search is what changes the result. */
const CANDIDATES_STALE_MS = 30 * 1000;
const CANDIDATES_PAGE_SIZE = 30;

/**
 * Which people a picker offers, one per kind of conversation:
 *
 *   - `internal` - colleagues only, the people a direct message is with.
 *   - `group` - colleagues and sales agents, what an internal group holds.
 *     Merchants never: the backend answers 400 for one.
 *   - `merchantGroup` - colleagues, sales and merchants of every role.
 *   - `support` - everybody outside, narrowed to one support tab by
 *     `supportAudience`.
 */
export type CandidatePool = 'internal' | 'group' | 'merchantGroup' | 'support';

/** The one flag that asks the backend for each pool. */
const POOL_PARAMS: Record<CandidatePool, GetConversationCandidatesParams> = {
  internal: { internalOnly: true },
  group: { group: true },
  merchantGroup: { merchantGroup: true },
  support: {},
};

/**
 * Who a conversation can be started with, or added to a group, from the pool
 * the caller names. `locationId` narrows any of them to the people of one
 * location.
 *
 * Paged, unlike the shortlist this used to be: with thousands of merchants the
 * first thirty are wherever the alphabet starts, and the people below them were
 * unreachable except by typing a name. Offset paging rather than the inbox's
 * keyset - that is what this endpoint offers - and the end of the list is a
 * page that came back short, since the response carries no total.
 */
export const useConversationCandidates = ({
  query,
  pool,
  supportAudience,
  locationId = null,
  enabled,
}: {
  query: string;
  pool: CandidatePool;
  supportAudience?: ConversationSupportAudience;
  locationId?: number | null;
  enabled: boolean;
}) => {
  const audience = pool === 'support' ? supportAudience ?? null : null;

  const infinite = useInfiniteQuery({
    queryKey: queryKeys.conversations.candidates(
      query || null,
      pool,
      audience,
      locationId
    ),
    queryFn: async ({ pageParam }) => {
      const { data } = await getConversationCandidates({
        ...(query ? { query } : {}),
        ...POOL_PARAMS[pool],
        ...(audience ? { supportAudience: audience } : {}),
        ...(locationId ? { locationId } : {}),
        page: pageParam,
        limit: CANDIDATES_PAGE_SIZE,
      });
      return data.data;
    },
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: ConversationCandidateResponse[],
      pages: ConversationCandidateResponse[][]
    ) =>
      lastPage.length < CANDIDATES_PAGE_SIZE ? undefined : pages.length + 1,
    enabled,
    staleTime: CANDIDATES_STALE_MS,
  });

  return {
    ...infinite,
    /** Flattened, because every call site wants one list of people. */
    data: infinite.data?.pages.flat(),
  };
};

/**
 * A conversation's members and metadata.
 *
 * The socket invalidates this key on `conversation_updated`, so a rename or a
 * new member lands here without a poll.
 */
export const useConversationDetail = ({
  conversationId,
  enabled,
}: {
  conversationId: number | null;
  enabled: boolean;
}) =>
  useQuery({
    queryKey: queryKeys.conversations.detail(conversationId ?? 0),
    queryFn: async () => {
      const { data } = await getConversation(conversationId as number);
      return data.data;
    },
    enabled: enabled && conversationId !== null,
  });

/**
 * The viewer's own thread with Viktor, as an inbox row.
 *
 * A POST rather than a GET, and idempotent: the first call creates the thread
 * and every later one returns the same row, which is the only way to learn
 * its id while it is still empty (an empty thread is left out of the list
 * endpoint). Runs as the chat page opens, since the card that leads into the
 * thread sits above every tab of the inbox.
 *
 * In between, the row is kept current by hand - the socket bumps its preview
 * and unread on `inbox_updated`, and `useChatRead` zeroes it - so the cached
 * copy is trusted for a while rather than re-posted on every render.
 */
export const useViktorConversation = ({ enabled }: { enabled: boolean }) =>
  useQuery({
    queryKey: queryKeys.conversations.viktor,
    queryFn: async () => {
      const { data } = await openViktorConversation();
      return data.data;
    },
    enabled,
    staleTime: 30 * 1000,
  });
